import React, { useEffect, useMemo, useState } from "react";
import {
  Loader2, Trash2, Plus, ChevronsUp, ChevronsDown, Eye, EyeOff,
  User, FolderOpen, X, Search,
} from "lucide-react";
import { adminApi, api as publicApi } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { useToast } from "../../hooks/use-toast";

const HOTELS = ["de", "com", "com.br", "es", "fi", "fr", "it", "nl", "com.tr"];

const previewUrl = (it) =>
  `https://www.habbo.${it.hotel || "de"}/habbo-imaging/avatarimage?user=${encodeURIComponent(it.item_key)}&direction=2&head_direction=2&size=m`;

/* ---------- Library Picker (Habbos) ---------- */
const LibraryPicker = ({ open, onClose, onPick }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");

  useEffect(() => {
    if (!open) return;
    let cancel = false;
    (async () => {
      setLoading(true);
      setItems([]);
      try {
        const data = await publicApi.listHabbos();
        if (!cancel) setItems(data.map((d) => ({
          type: "habbo",
          item_key: d.name,
          label: d.name,
          role: d.role || "",
          category: d.category || "",
          hotel: d.hotel || "de",
          preview: `https://www.habbo.${d.hotel || "de"}/habbo-imaging/avatarimage?user=${encodeURIComponent(d.name)}&direction=2&head_direction=2&size=m`,
        })));
      } catch (e) { /* silent */ }
      finally { if (!cancel) setLoading(false); }
    })();
    return () => { cancel = true; };
  }, [open]);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return items;
    return items.filter((it) =>
      (it.item_key || "").toLowerCase().includes(term) ||
      (it.label || "").toLowerCase().includes(term)
    );
  }, [items, q]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={onClose} data-testid="hero-picker-modal">
      <div
        className="w-full max-w-4xl max-h-[85vh] overflow-hidden rounded-2xl border border-white/10 bg-[#0f141b] shadow-2xl flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-white/10 p-4">
          <div className="flex items-center gap-2">
            <FolderOpen className="h-4 w-4 text-[#b39bff]" />
            <h3 className="text-base font-semibold text-white">Habbo aus Bibliothek waehlen</h3>
          </div>
          <Button onClick={onClose} className="btn-ghost rounded-md p-1.5" data-testid="hero-picker-close">
            <X className="h-4 w-4" />
          </Button>
        </div>

        <div className="flex items-center gap-2 border-b border-white/10 px-3 py-2">
          <Search className="h-3.5 w-3.5 text-zinc-500" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Habbo-Name suchen..."
            className="h-8 w-64 bg-[#10141b] border-white/10 text-xs text-zinc-200"
            data-testid="hero-picker-search"
          />
          <div className="ml-auto text-[11px] text-zinc-500">{filtered.length} Habbos</div>
        </div>

        <div className="flex-1 overflow-y-auto p-3">
          {loading ? (
            <div className="flex items-center justify-center py-16 text-zinc-400">
              <Loader2 className="h-5 w-5 animate-spin" />
            </div>
          ) : filtered.length === 0 ? (
            <div className="py-16 text-center text-sm text-zinc-500">Keine Habbos.</div>
          ) : (
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6">
              {filtered.map((it) => (
                <button
                  key={it.item_key}
                  onClick={() => onPick(it)}
                  data-testid={`hero-picker-item-${it.item_key}`}
                  className="group flex flex-col items-center gap-1 rounded-lg border border-white/10 bg-[#0a0d12] p-2 text-left transition-colors hover:border-[#7c4dff]/50 hover:bg-[#7c4dff]/5"
                >
                  <div className="flex h-20 w-full items-center justify-center">
                    <img
                      src={it.preview}
                      alt={it.label}
                      className="hb-pixel-img max-h-16 max-w-full"
                      loading="lazy"
                      onError={(e) => { e.currentTarget.style.opacity = "0.2"; }}
                    />
                  </div>
                  <div className="w-full truncate text-[10px] text-zinc-300" title={it.item_key}>
                    {it.item_key}
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

/* ---------- Hauptkomponente ---------- */
const HeroPanel = ({ token }) => {
  const a = useMemo(() => adminApi(token), [token]);
  const { toast } = useToast();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ item_key: "", label: "", hotel: "de" });
  const [pickerOpen, setPickerOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const all = await a.listHeroFloaters();
      // Iter51b: nur Habbos anzeigen / verwalten – Legacy-Badges/Furni ausgeblendet.
      setItems((all || []).filter((x) => x.type === "habbo"));
    } catch (e) { toast({ title: "Laden fehlgeschlagen", description: String(e), variant: "destructive" }); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const addItem = async (payload) => {
    await a.createHeroFloater({
      type: "habbo",
      item_key: payload.item_key.trim(),
      label: (payload.label || "").trim(),
      role: payload.role || "",
      category: payload.category || "",
      hotel: payload.hotel || "de",
      enabled: true,
      order: 100,
    });
  };

  const onAdd = async () => {
    if (!form.item_key.trim()) { toast({ title: "Bitte Habbo-Namen angeben", variant: "destructive" }); return; }
    setAdding(true);
    try {
      await addItem(form);
      setForm({ item_key: "", label: "", hotel: form.hotel });
      toast({ title: "Hinzugefuegt" });
      await load();
    } catch (err) {
      toast({ title: "Fehler", description: err?.response?.data?.detail || String(err), variant: "destructive" });
    } finally { setAdding(false); }
  };

  const onPickFromLibrary = async (picked) => {
    if (items.some((x) => x.item_key === picked.item_key)) {
      toast({ title: "Bereits konfiguriert", description: `${picked.item_key} ist schon dabei.` });
      return;
    }
    try {
      await addItem(picked);
      toast({ title: "Hinzugefuegt", description: picked.item_key });
      await load();
    } catch (err) {
      toast({ title: "Fehler", description: err?.response?.data?.detail || String(err), variant: "destructive" });
    }
  };

  const onDelete = async (it) => {
    if (!window.confirm(`"${it.label || it.item_key}" wirklich entfernen?`)) return;
    try { await a.deleteHeroFloater(it.id); await load(); toast({ title: "Entfernt" }); }
    catch (e) { toast({ title: "Fehler", description: String(e), variant: "destructive" }); }
  };
  const onToggle = async (it) => {
    try { await a.updateHeroFloater(it.id, { enabled: !it.enabled }); await load(); }
    catch (e) { toast({ title: "Fehler", description: String(e), variant: "destructive" }); }
  };
  const onMove = async (it, direction) => {
    const idx = items.findIndex((x) => x.id === it.id);
    const other = items[direction === "up" ? idx - 1 : idx + 1];
    if (!other) return;
    try {
      await Promise.all([
        a.updateHeroFloater(it.id, { order: other.order }),
        a.updateHeroFloater(other.id, { order: it.order }),
      ]);
      await load();
    } catch (e) { toast({ title: "Fehler", description: String(e), variant: "destructive" }); }
  };

  return (
    <div className="space-y-4" data-testid="hero-panel">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <h2 className="text-xl font-bold text-white">Startseite – Schwebende Habbos</h2>
          <p className="mt-1 text-sm text-zinc-400">
            Konfiguriere, welche Habbos auf der Startseite schweben. Solange diese Liste nicht leer ist, wird sie verwendet. Bei leerer Liste laeuft die alte zufaellige Auswahl.
          </p>
        </div>
        <Button
          onClick={() => setPickerOpen(true)}
          className="btn-primary rounded-lg px-3 py-2 text-xs"
          data-testid="hero-open-picker"
        >
          <FolderOpen className="mr-1 h-4 w-4" /> Aus Bibliothek waehlen
        </Button>
      </div>

      {/* Manual Add-Form */}
      <div className="grid grid-cols-1 gap-2 rounded-xl border border-white/10 bg-[#0f141b] p-3 md:grid-cols-[1fr_1fr_140px_auto]">
        <Input
          value={form.item_key}
          onChange={(e) => setForm((f) => ({ ...f, item_key: e.target.value }))}
          placeholder="Habbo-Name, z.B. :-O"
          className="bg-[#10141b] border-white/10 text-zinc-200"
          data-testid="hero-add-key"
        />
        <Input
          value={form.label}
          onChange={(e) => setForm((f) => ({ ...f, label: e.target.value }))}
          placeholder="Label (optional)"
          className="bg-[#10141b] border-white/10 text-zinc-200"
          data-testid="hero-add-label"
        />
        <select
          value={form.hotel}
          onChange={(e) => setForm((f) => ({ ...f, hotel: e.target.value }))}
          className="rounded-md border border-white/10 bg-[#10141b] px-2 py-2 text-sm text-zinc-200"
          data-testid="hero-add-hotel"
        >
          {HOTELS.map((h) => (
            <option key={h} value={h}>habbo.{h}</option>
          ))}
        </select>
        <Button onClick={onAdd} disabled={adding} className="btn-primary rounded-lg px-3 py-2 text-xs" data-testid="hero-add-btn">
          {adding ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Plus className="mr-1 h-4 w-4" /> Hinzufuegen</>}
        </Button>
      </div>

      {/* Liste */}
      {loading ? (
        <div className="flex items-center justify-center py-16 text-zinc-400"><Loader2 className="h-5 w-5 animate-spin" /></div>
      ) : items.length === 0 ? (
        <div className="rounded-xl border border-dashed border-white/10 bg-[#0f141b] p-10 text-center text-sm text-zinc-500" data-testid="hero-empty">
          Noch nichts konfiguriert. Die Startseite zeigt eine zufaellige Auswahl. Fuege hier Habbos hinzu, um die Anzeige festzulegen.
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3" data-testid="hero-list">
          {items.map((it, idx) => (
            <div key={it.id} className={`relative rounded-lg border bg-[#0f141b] p-2 ${it.enabled ? "border-white/10" : "border-zinc-700/40 opacity-60"}`} data-testid={`hero-item-${it.id}`}>
              <div className="flex h-24 items-center justify-center rounded bg-[#0a0d12]">
                <img
                  src={previewUrl(it)}
                  alt={it.label || it.item_key}
                  className="hb-pixel-img max-h-20 max-w-full"
                  onError={(e) => { e.currentTarget.style.opacity = "0.2"; }}
                />
              </div>
              <div className="mt-2 flex items-center gap-1 text-[11px] text-zinc-300">
                <User className="h-3 w-3 text-[#b39bff]" />
                <span className="truncate font-mono" title={it.item_key}>{it.item_key}</span>
              </div>
              {it.label && it.label !== it.item_key && (
                <div className="truncate text-[10px] text-zinc-500" title={it.label}>{it.label}</div>
              )}
              <div className="mt-2 flex items-center justify-between gap-1">
                <Button onClick={() => onMove(it, "up")} disabled={idx === 0} className="btn-ghost rounded-md p-1.5" title="Nach oben">
                  <ChevronsUp className="h-3 w-3" />
                </Button>
                <Button onClick={() => onMove(it, "down")} disabled={idx === items.length - 1} className="btn-ghost rounded-md p-1.5" title="Nach unten">
                  <ChevronsDown className="h-3 w-3" />
                </Button>
                <Button onClick={() => onToggle(it)} className="btn-ghost rounded-md p-1.5" title={it.enabled ? "Deaktivieren" : "Aktivieren"} data-testid={`hero-toggle-${it.id}`}>
                  {it.enabled ? <Eye className="h-3 w-3 text-emerald-300" /> : <EyeOff className="h-3 w-3 text-zinc-500" />}
                </Button>
                <Button onClick={() => onDelete(it)} className="btn-ghost rounded-md p-1.5 text-rose-300 hover:border-rose-500/40" title="Loeschen" data-testid={`hero-delete-${it.id}`}>
                  <Trash2 className="h-3 w-3" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <LibraryPicker open={pickerOpen} onClose={() => setPickerOpen(false)} onPick={onPickFromLibrary} />
    </div>
  );
};

export default HeroPanel;
